import React, { useState, useEffect, ChangeEvent, ReactNode } from 'react';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import TabPanel from 'components/UI/TabPanel';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Main, Text, Background } from 'styles/themeColors';
import { BorderRadius } from 'styles/themeBorderRadius';
import { MainColors } from 'types/enumTypes';
import { Space } from 'styles/themeSpaces';
import { Height } from 'styles/themeHeights';
import NavLink from 'view/NavLink';
import { navigate } from 'hooks/useHookRouterCompat';
import { TAB_KEYS, WAREHOUSE_ERROR } from 'constants/product';

const TabsContainer = styled.div<any>`
  .app-bar {
    background-color: ${Background.white};
    border: none;
    box-shadow: none;
  }
`;

const StyledTabs = muiStyled(Tabs, {
  shouldForwardProp: (prop) => prop !== '$theme',
})<{ $theme?: string }>(({ $theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  borderBottom: '1px solid !important',
  borderColor: `${Main[$theme as keyof typeof Main]} !important`,
  '& .Mui-selected': {
    backgroundColor: '#FC780B !important',
  },
}));

const StyledTab = muiStyled(Tab, {
  shouldForwardProp: (prop) => !['$theme', '$active'].includes(prop as string),
})<{ $theme?: string; $active?: boolean; color?: string; borderRadius?: string; padding?: string; margin?: string }>(({ color, borderRadius: br, padding: p, margin: m, $active, $theme }) => ({
  color: `${color} !important`,
  minWidth: '10px !important',
  maxWidth: '240px !important',
  minHeight: '36px !important',
  opacity: '1 !important',
  borderRadius: br ? `${br} !important` : `${BorderRadius.m} ${BorderRadius.m} 0 0 !important`,
  paddingTop: p ? `${p} !important` : '0px !important',
  paddingBottom: p ? `${p} !important` : '0px !important',
  margin: m ? `${m} !important` : '0px !important',
  ...(!$active && {
    '&:hover': {
      backgroundColor: $theme === 'accent'
        ? 'rgba(73, 141, 218, 0.1) !important'
        : `${Main['brandLightest']} !important`,
    },
  }),
  '& .MuiTab-wrapper div': {
    alignItems: 'center !important',
  },
  '&.Mui-disabled': {
    opacity: '0.5 !important',
    cursor: 'not-allowed !important',
    pointerEvents: 'none !important',
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
  },
}));

// Don't pass color and bgColor props to MaterialUI Badge component to avoid console warnings
const StyledBadge = muiStyled(Badge, {
  shouldForwardProp: (prop) => prop !== 'bgColor',
})<any>(({ bgColor, color }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: bgColor,
    color: color,
    minWidth: '20px',
    position: 'static',
    borderRadius: `${BorderRadius.m} !important`,
    marginLeft: Space.spHalf,
  },
  '& .MuiBadge-anchorOriginTopRightRectangular': {
    transform: 'none !important',
  },
}));

interface TabBadge {
  content: string;
  color: string;
  backgroundColor: string;
};

interface Tab {
  /** Stable identity — the rendered list is filtered per product type, so indexes shift. */
  tabKey?: string;
  value: string;
  badge: TabBadge;
  link: string
  disabled?: boolean
};

interface Props {
  className?: string;
  tabs: Tab[];
  activeTab?: number;
  margin?: string;
  padding?: string;
  height?: string;
  borderRadius?: string;
  tabPanelPadding?: string;
  theme?: MainColors;
  setActiveTab?: (number: number | undefined) => void;
  canGoToNextTab?: (tabNum: number) => boolean;
  variantErrorsCount?: number;
  warehouseErrorCount?: number;
  loading?: boolean
}

const TabsBarComponent = ({
  className = '',
  tabs,
  activeTab = 0,
  margin = '0',
  padding,
  height = Height.m,
  borderRadius,
  theme = 'accent' as MainColors,
  setActiveTab,
  canGoToNextTab,
  variantErrorsCount,
  warehouseErrorCount,
  loading = false
}: Props) => {
  const [value, setValue] = useState(0);

  const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
    newValue !== 0 && canGoToNextTab && canGoToNextTab(newValue) && setValue(newValue);
    if (setActiveTab) {
      setActiveTab(newValue);
    }
  };

  useEffect(() => {
    if (activeTab || activeTab === 0) {
      setValue(activeTab);
    }
  }, [activeTab]);

  return (
    <TabsContainer>
      <AppBar
        position="static"
        className="app-bar"
        style={{ padding, margin, height }}
      >
        <StyledTabs
          value={value}
          onChange={handleChange}
          aria-label="Tabs"
          TabIndicatorProps={{ style: { display: 'none' } }}
          $theme={theme}
        >
          {
            tabs.map((tab, index) => {
              const badge = tab.badge.content ? (
                <StyledBadge
                  badgeContent={tab.badge.content}
                  color={tab.badge.color}
                  bgColor={tab.badge.backgroundColor}
                  classes={{ badge: 'badge' }}
                />
              ) : null;

              const renderTab = (
                <FlexboxContainer
                  alignItems='center'
                >
                  {tab.value}
                  {badge}
                  {tab.tabKey === TAB_KEYS.VARIANTS && variantErrorsCount ? <p className='error-badge'> {variantErrorsCount} </p> : ''}
                  {tab.tabKey === TAB_KEYS.WAREHOUSES && warehouseErrorCount && !loading ? <p className='error-badge'> {warehouseErrorCount} </p> : ''}
                </FlexboxContainer>
              );

              return (
                <StyledTab
                  onClick={() => {
                      // Check if we can navigate to this tab before navigating
                      if (canGoToNextTab && !canGoToNextTab(index)) {
                        // Validation failed, don't navigate
                        return;
                      }
                    navigate(tab.link)
                  }}
                  disabled={tab.disabled}
                  className={`tab ${className}`}
                  key={index}
                  label={renderTab}
                  color={value === index ? Text.whitePrimary : Text.secondary}
                  $theme={theme}
                  padding={padding}
                  margin={margin}
                  borderRadius={borderRadius}
                  $active={value === index}
                  {...a11yProps(index)}
                />
              )
            })
          }
        </StyledTabs>
      </AppBar>
    </TabsContainer>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `company-tab-${index}`,
    'aria-controls': `company-tabpanel-${index}`,
  };
};

export default TabsBarComponent;
